import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form.tsx';
import { Input } from '@/components/ui/input.tsx';
import { useRef, useState } from 'react';
import { EyePasswordShow } from '@/assets/auth/eye-password-show.tsx';
import { EyePasswordHide } from '@/assets/auth/eye-password-hide.tsx';
import { TRegisterFormSchema } from './steps.tsx';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { PasswordRequirements } from '@/features/auth/components/password-requirements.tsx';

export const BasicDetails = () => {
  const [showPassword, setShowPassword] = useState(false);
  const form = useFormContext<TRegisterFormSchema>();
  const { t } = useTranslation();
  const passwordInputRef = useRef<HTMLInputElement>(null);

  const scrollToInput = () => {
    if (passwordInputRef.current) {
      const yOffset = -100;
      const element = passwordInputRef.current;
      const y = element.getBoundingClientRect().top + window.scrollY + yOffset;

      setTimeout(() => {
        window.scrollTo({
          top: y,
          behavior: 'smooth',
        });
      }, 300);
    }
  };

  return (
    <div className="space-y-4">
      <h2 className="text-2xl font-bold">{t('auth.register.title')}</h2>
      <div className="grid grid-cols-2 gap-4">
        <FormField
          control={form.control}
          name="basicDetails.firstName"
          render={({ field }) => (
            <FormItem>
              <FormLabel htmlFor="firstName" className="text-[16px]">
                {t('auth.register.firstName')}
              </FormLabel>
              <FormControl>
                <Input
                  {...field}
                  id="firstName"
                  placeholder={t('auth.register.firstNamePlaceholder')}
                  minLength={3}
                  required
                  className="text-[16px]"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="basicDetails.lastName"
          render={({ field }) => (
            <FormItem>
              <FormLabel htmlFor="lastName" className="text-[16px]">
                {t('auth.register.lastName')}
              </FormLabel>
              <FormControl>
                <Input
                  {...field}
                  id="lastName"
                  placeholder={t('auth.register.lastNamePlaceholder')}
                  minLength={3}
                  required
                  className="text-[16px]"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
      <FormField
        control={form.control}
        name="basicDetails.email"
        render={({ field }) => (
          <FormItem>
            <FormLabel htmlFor="email" className="text-[16px]">
              {t('auth.register.email')}
            </FormLabel>
            <FormControl>
              <Input
                {...field}
                id="email"
                type="email"
                autoComplete="email-address"
                placeholder={t('auth.register.emailPlaceholder')}
                required
                className="text-[16px]"
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="basicDetails.password"
        render={({ field }) => (
          <FormItem>
            <FormLabel htmlFor="password" className="text-[16px]">
              {t('auth.register.password')}
            </FormLabel>
            <FormControl>
              <div className="relative">
                <Input
                  {...field}
                  ref={passwordInputRef}
                  id="password"
                  type={showPassword ? 'string' : 'password'}
                  autoComplete="current-password"
                  placeholder={t('auth.register.passwordPlaceholder')}
                  className="text-[16px]"
                  required
                  minLength={8}
                  onFocus={scrollToInput}
                />
                <div
                  className="absolute top-1 right-4 rounded-lg p-1 transition-colors hover:bg-[#e2e8f0]"
                  onClick={() => setShowPassword((s) => !s)}
                >
                  {showPassword ? (
                    <EyePasswordShow width="19" height="19" />
                  ) : (
                    <EyePasswordHide width="19" height="19" />
                  )}
                </div>
              </div>
            </FormControl>
            <PasswordRequirements control={form.control} watch="basicDetails.password" />
            <FormMessage />
          </FormItem>
        )}
      />
    </div>
  );
};
